import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  beverageAuthorizationsApi,
  beverageDashboardApi,
  beverageInspectionsApi,
  handleApiError,
  type BeverageAuthorizationListParams,
} from '../lib/api-client';
import type {
  BeverageAuthorizationStatus,
  BeverageEventPurpose,
  BeverageInspectionPhase,
  CreateBeverageInspectionDto,
} from '../types/api';

const ONE_MINUTE = 60 * 1000;

export const useBeverageAuthorizations = (params?: BeverageAuthorizationListParams) => {
  return useQuery({
    queryKey: ['beverage-authorizations', params ?? {}],
    queryFn: () => beverageAuthorizationsApi.list(params),
    staleTime: ONE_MINUTE,
    refetchOnWindowFocus: false,
  });
};

export const useBeverageAuthorization = (id: string | undefined) => {
  return useQuery({
    queryKey: ['beverage-authorizations', id],
    queryFn: () => beverageAuthorizationsApi.getById(id!),
    enabled: !!id,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};

/** The dynamic questionnaire (sections/questions/skip-logic) for a given inspection phase. */
export const useBeverageInspectionQuestionnaire = (phase: BeverageInspectionPhase | undefined) => {
  return useQuery({
    queryKey: ['beverage-inspection-questionnaire', phase],
    queryFn: () => beverageInspectionsApi.getCurrentQuestionnaire(phase!),
    enabled: !!phase,
    staleTime: 5 * ONE_MINUTE,
    refetchOnWindowFocus: false,
  });
};

export const useBeverageDashboard = (params?: {
  locationId?: string;
  from?: string;
  to?: string;
  beverageTypeId?: string;
  purpose?: BeverageEventPurpose;
  status?: BeverageAuthorizationStatus;
  groupBy?: 'beverageType' | 'purpose';
}) => {
  return useQuery({
    queryKey: ['beverage-dashboard', params ?? {}],
    queryFn: () => beverageDashboardApi.get(params),
    staleTime: 5 * ONE_MINUTE,
    refetchOnWindowFocus: false,
  });
};

export const useAcknowledgeBeverageAuthorization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => beverageAuthorizationsApi.acknowledge(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['beverage-authorizations'] });
      queryClient.invalidateQueries({ queryKey: ['beverage-authorizations', id] });
    },
    onError: handleApiError,
  });
};

/** Records a PRE_EVENT, IN_EVENT, or POST_EVENT inspection — phase comes from `dto.phase`. */
export const useCreateBeverageInspection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: CreateBeverageInspectionDto }) =>
      beverageInspectionsApi.create(id, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['beverage-authorizations'] });
      queryClient.invalidateQueries({ queryKey: ['beverage-authorizations', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['beverage-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['beverage-monitoring'] });
    },
  });
};
